import { useState } from 'react';
import { ArrowRight, GraduationCap, QrCode, Zap } from 'lucide-react';
import { HashLink } from '../components/HashLink';
import CategoryChips from '../components/CategoryChips';
import ProductGrid from '../components/ProductGrid';
import ResultsToolbar from '../components/ResultsToolbar';
import SearchInput from '../components/SearchInput';
import type { ProductSort } from '../lib/api';
import { useProducts } from '../lib/queries';
import { useDebounce } from '../lib/useDebounce';

function Hero() {
  return (
    <section id="hero" className="mx-auto max-w-6xl px-4 pt-10 md:pt-14">
      <div>
        <div className="board relative overflow-hidden rounded-2xl px-6 py-10 md:px-12 md:py-14">
          {/* Jediný uzavřený křídový rámeček dokola. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-3 rounded-xl border border-dashed border-chalk/20"
          />

          <div className="relative lg:grid lg:grid-cols-2 lg:items-center lg:gap-10">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-accent">
                <span className="h-0.5 w-6 rounded bg-accent" />
                Matematika a fyzika · 2. stupeň ZŠ
              </p>
              <h1 className="max-w-2xl text-[32px] leading-[1.1] text-chalk md:text-[44px]">
                Procvičování, které děti{' '}
                <span className="relative whitespace-nowrap">
                  <span className="relative z-10">baví</span>
                  <svg
                    className="absolute -bottom-1 left-0 z-0 h-[0.42em] w-full"
                    viewBox="0 0 200 24"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 16 C 40 8, 90 20, 130 12 S 190 10, 197 14"
                      fill="none"
                      stroke="#F6C445"
                      strokeWidth={9}
                      strokeLinecap="round"
                      opacity={0.85}
                    />
                  </svg>
                </span>
              </h1>
              <p className="mt-5 max-w-[48ch] text-chalk/90">
                Materiály z&nbsp;matematiky a&nbsp;fyziky, které jsem vytvořila pro své žáky. Hotové
                k&nbsp;vytištění, s&nbsp;náhledem u&nbsp;každého a&nbsp;doručením e-mailem.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <HashLink
                  to="#katalog"
                  className="focus-ring-chalk group inline-flex min-h-[48px] items-center gap-2.5 rounded-xl bg-accent px-6 py-3 font-semibold text-ink transition hover:brightness-[0.97] active:scale-[.98]"
                >
                  Vybrat materiál
                  <ArrowRight
                    className="h-[18px] w-[18px] transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2}
                  />
                </HashLink>
                <HashLink
                  to="#jak-nakup-probiha"
                  className="focus-ring-chalk rounded font-semibold text-chalk underline decoration-chalk/50 decoration-dashed underline-offset-4 hover:decoration-accent"
                >
                  Jak nákup probíhá
                </HashLink>
              </div>
            </div>

            {/* Prodejní vizuál se všemi produkty (průhledné PNG na tabuli). */}
            <div className="mt-8 lg:mt-0">
              <img
                src="/hero/banner.webp"
                alt="Ukázky materiálů: výukové domino Pohyb a Síla a kartičky Tlak kolem nás"
                width={1200}
                height={900}
                className="mx-auto w-full max-w-lg drop-shadow-2xl lg:max-w-none"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBand() {
  const items = [
    { icon: Zap, text: 'Soubor máte v e-mailu do pár minut od zaplacení.' },
    { icon: QrCode, text: 'QR platba převodem bez poplatku, přímo z bankovní aplikace.' },
    {
      icon: GraduationCap,
      text: 'Materiály připravuje učitelka matematiky a fyziky ze své praxe.',
    },
  ];
  return (
    <section className="relative z-10 mx-auto -mt-7 max-w-5xl px-4">
      <div className="rounded-card border border-line bg-card shadow-card">
        <ul className="no-scrollbar mask-fade-x md:mask-none flex snap-x snap-mandatory overflow-x-auto md:grid md:grid-cols-3 md:overflow-visible">
          {items.map(({ icon: Icon, text }) => (
            <li
              key={text}
              className="flex min-w-[82%] snap-start items-start gap-2.5 border-l border-line p-4 first:border-l-0 md:min-w-0"
            >
              <Icon className="mt-0.5 h-[18px] w-[18px] flex-none text-board" strokeWidth={2} />
              <span className="text-sm text-ink-soft">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Catalog() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ProductSort>('newest');
  const q = useDebounce(search.trim(), 350);
  const query = useProducts({ ...(q ? { q } : {}), sort, limit: 24 });

  return (
    <section id="katalog" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-10 md:py-14">
      <h2 className="mb-5 text-[1.6rem] md:text-3xl">Katalog materiálů</h2>

      <div className="mb-4 max-w-xl">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      <div className="mb-4">
        <CategoryChips />
      </div>

      <ResultsToolbar
        count={query.data?.total}
        loading={query.isLoading}
        query={q}
        sort={sort}
        onSortChange={setSort}
      />

      <ProductGrid
        data={query.data}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        emptyLabel={q ? `Nic jsme nenašli pro „${q}"` : 'Zatím tu nejsou žádné materiály'}
      />
    </section>
  );
}

function HowItWorks() {
  const steps = [
    'Vyberete si materiál. Každý si předem prohlédnete v náhledech.',
    'Zaplatíte QR kódem bez poplatku, přímo z bankovní aplikace.',
    'Odkaz ke stažení vám přijde e-mailem, obvykle během pár minut.',
  ];
  return (
    <section id="jak-nakup-probiha" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-10 md:py-12">
      <h2 className="text-xl md:text-2xl">Jak nákup probíhá</h2>
      <ol className="mt-5 grid gap-4 sm:grid-cols-3">
        {steps.map((text, i) => (
          <li
            key={text}
            className="flex items-start gap-3 rounded-card border border-line bg-card p-4 shadow-card"
          >
            <span
              className="grid h-8 w-8 flex-none place-items-center rounded-full bg-board font-serif text-base font-bold text-chalk"
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <p className="text-sm text-ink-soft">{text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <TrustBand />
      <div className="grid-paper">
        <Catalog />
      </div>
      <HowItWorks />
    </div>
  );
}
